
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, UserPlus, Loader2, Pencil, Check, X as XIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DormitoryVisits {
  dormitory: string;
  dormitory_mother: string;
  dormitory_mother_contact: string;
  total_visits: number;
  total_students: number;
}

interface EditDialogProps {
  dormitory: DormitoryVisits;
  onClose: () => void;
  isOpen: boolean;
}

interface StudentForAssignment {
  id: string;
  surname: string;
  other_names: string | null;
  class: string | null;
  stream: string | null;
  dormitory: string | null;
  sex: string | null;
}

const EditDormitoryDialog = ({ dormitory, onClose, isOpen }: EditDialogProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [motherName, setMotherName] = useState(dormitory.dormitory_mother || "");
  const [motherContact, setMotherContact] = useState(dormitory.dormitory_mother_contact || "");

  const updateDormitoryMother = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("students")
        .update({
          dormitory_mother: motherName,
          dormitory_mother_contact: motherContact,
        })
        .eq("school_id", user?.schoolId)
        .eq("dormitory", dormitory.dormitory);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Dormitory mother updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      onClose();
    },
    onError: (error) => {
      console.error("Error updating dormitory mother:", error);
      toast({
        title: "Error",
        description: "Failed to update dormitory mother",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDormitoryMother.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Dormitory Mother</DialogTitle>
            <DialogDescription>
              Update the dormitory mother details for {dormitory.dormitory}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="mother-name">Mother's Name</Label>
              <Input
                id="mother-name"
                value={motherName}
                onChange={(e) => setMotherName(e.target.value)}
                placeholder="Enter mother's name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="mother-contact">Contact Number</Label>
              <Input
                id="mother-contact"
                value={motherContact}
                onChange={(e) => setMotherContact(e.target.value)}
                placeholder="Enter contact number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateDormitoryMother.isPending}
            >
              {updateDormitoryMother.isPending ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface AssignStudentsDialogProps {
  dormitory: DormitoryVisits;
  onClose: () => void;
  isOpen: boolean;
}

type AssignStep = "primary" | "review" | "secondary";
type AssignMode = "individual" | "bulk";

const AssignStudentsDialog = ({ dormitory, onClose, isOpen }: AssignStudentsDialogProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<AssignMode>("individual");
  const [step, setStep] = useState<AssignStep>("primary");
  const [individualSearch, setIndividualSearch] = useState("");
  const [individualSelected, setIndividualSelected] = useState<Set<string>>(new Set());
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSex, setSelectedSex] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [primarySelected, setPrimarySelected] = useState<Set<string>>(new Set());
  const [secondarySelected, setSecondarySelected] = useState<Set<string>>(new Set());
  const [reviewSearch, setReviewSearch] = useState("");
  // Dorm mother inline editing
  const [editingMother, setEditingMother] = useState(false);
  const [motherName, setMotherName] = useState(dormitory.dormitory_mother || "");
  const [motherContact, setMotherContact] = useState(dormitory.dormitory_mother_contact || "");

  const updateMotherMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("students")
        .update({
          dormitory_mother: motherName,
          dormitory_mother_contact: motherContact,
        })
        .eq("school_id", user?.schoolId)
        .eq("dormitory", dormitory.dormitory);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Dormitory mother updated" });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      setEditingMother(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    },
  });

  // Fetch distinct classes
  const { data: classes } = useQuery({
    queryKey: ["distinct-classes", user?.schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("class")
        .eq("school_id", user?.schoolId!)
        .eq("is_archived", false)
        .not("class", "is", null);
      if (error) throw error;
      const unique = [...new Set(data.map((d) => d.class).filter(Boolean))] as string[];
      return unique.sort();
    },
    enabled: isOpen && !!user?.schoolId,
  });

  // Fetch all non-archived students (paginated)
  const { data: allStudents, isLoading } = useQuery({
    queryKey: ["students-for-dorm-assignment", user?.schoolId],
    queryFn: async () => {
      let allData: StudentForAssignment[] = [];
      let page = 0;
      const pageSize = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("students")
          .select("id, surname, other_names, class, stream, dormitory, sex")
          .eq("school_id", user?.schoolId!)
          .eq("is_archived", false)
          .range(page * pageSize, (page + 1) * pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allData = allData.concat(data as any);
        if (data.length < pageSize) break;
        page++;
      }
      return allData;
    },
    enabled: isOpen && !!user?.schoolId,
  });

  // Primary candidates: students matching selected class + sex
  const primaryCandidates = useMemo(() => {
    if (!allStudents || !selectedClass || !selectedSex) return [];
    return allStudents
      .filter((s) => s.class === selectedClass && (s as any).sex === selectedSex)
      .sort((a, b) => a.surname.localeCompare(b.surname));
  }, [allStudents, selectedClass, selectedSex]);

  // When candidates change, auto-select all
  const handlePreview = () => {
    const ids = new Set(primaryCandidates.map((s) => s.id));
    setPrimarySelected(ids);
    setStep("review");
  };

  // Separate candidates into groups
  const { alreadyInThisDorm, alreadyInOtherDorm, unassigned } = useMemo(() => {
    const inThis: StudentForAssignment[] = [];
    const inOther: StudentForAssignment[] = [];
    const none: StudentForAssignment[] = [];
    primaryCandidates.forEach((s) => {
      if (s.dormitory === dormitory.dormitory) inThis.push(s);
      else if (s.dormitory && s.dormitory.trim()) inOther.push(s);
      else none.push(s);
    });
    return { alreadyInThisDorm: inThis, alreadyInOtherDorm: inOther, unassigned: none };
  }, [primaryCandidates, dormitory.dormitory]);

  // Secondary candidates: all students NOT in primary candidates, matching search
  const secondaryCandidates = useMemo(() => {
    if (!allStudents) return [];
    const primaryIds = new Set(primaryCandidates.map((s) => s.id));
    const query = searchQuery.toLowerCase().trim();
    return allStudents
      .filter((s) => !primaryIds.has(s.id))
      .filter((s) => {
        if (!query) return true;
        const fullName = `${s.surname} ${s.other_names || ""}`.toLowerCase();
        const cls = (s.class || "").toLowerCase();
        return fullName.includes(query) || cls.includes(query);
      })
      .sort((a, b) => a.surname.localeCompare(b.surname));
  }, [allStudents, primaryCandidates, searchQuery]);

  const togglePrimary = (id: string) => {
    setPrimarySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSecondary = (id: string) => {
    setSecondarySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const assignMutation = useMutation({
    mutationFn: async () => {
      const allIds = [...primarySelected, ...secondarySelected];
      if (allIds.length === 0) return;
      const { error } = await supabase
        .from("students")
        .update({
          dormitory: dormitory.dormitory,
          dormitory_mother: dormitory.dormitory_mother || null,
          dormitory_mother_contact: dormitory.dormitory_mother_contact || null,
        })
        .in("id", allIds);
      if (error) throw error;
    },
    onSuccess: () => {
      const total = primarySelected.size + secondarySelected.size;
      toast({
        title: "Success",
        description: `${total} student(s) assigned to ${dormitory.dormitory}`,
      });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      queryClient.invalidateQueries({ queryKey: ["students-for-dorm-assignment"] });
      resetAndClose();
    },
    onError: (error) => {
      console.error("Error assigning students:", error);
      toast({ title: "Error", description: "Failed to assign students", variant: "destructive" });
    },
  });

  // Individual mode: filtered candidates
  const individualCandidates = useMemo(() => {
    if (!allStudents || !individualSearch.trim()) return [];
    const query = individualSearch.toLowerCase().trim();
    return allStudents
      .filter((s) => {
        const fullName = `${s.surname} ${s.other_names || ""}`.toLowerCase();
        const cls = (s.class || "").toLowerCase();
        return fullName.includes(query) || cls.includes(query);
      })
      .sort((a, b) => a.surname.localeCompare(b.surname))
      .slice(0, 50);
  }, [allStudents, individualSearch]);

  const toggleIndividual = (id: string) => {
    setIndividualSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const assignIndividualMutation = useMutation({
    mutationFn: async () => {
      const allIds = [...individualSelected];
      if (allIds.length === 0) return;
      const { error } = await supabase
        .from("students")
        .update({
          dormitory: dormitory.dormitory,
          dormitory_mother: dormitory.dormitory_mother || null,
          dormitory_mother_contact: dormitory.dormitory_mother_contact || null,
        })
        .in("id", allIds);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: `${individualSelected.size} student(s) assigned to ${dormitory.dormitory}`,
      });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      queryClient.invalidateQueries({ queryKey: ["students-for-dorm-assignment"] });
      resetAndClose();
    },
    onError: (error) => {
      console.error("Error assigning students:", error);
      toast({ title: "Error", description: "Failed to assign students", variant: "destructive" });
    },
  });

  const resetAndClose = () => {
    setMode("individual");
    setStep("primary");
    setSelectedClass("");
    setSelectedSex("");
    setSearchQuery("");
    setReviewSearch("");
    setPrimarySelected(new Set());
    setSecondarySelected(new Set());
    setIndividualSearch("");
    setIndividualSelected(new Set());
    onClose();
  };

  const renderStudentRow = (
    student: StudentForAssignment,
    isChecked: boolean,
    onToggle: () => void,
    showDormWarning?: boolean
  ) => (
    <label
      key={student.id}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 cursor-pointer"
    >
      <Checkbox checked={isChecked} onCheckedChange={onToggle} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {student.surname} {student.other_names || ""}
        </p>
        <p className="text-xs text-muted-foreground">
          {student.class || "No class"}{student.stream ? ` - ${student.stream}` : ""}
          {" | "}{(student as any).sex || "?"}
        </p>
      </div>
      {showDormWarning && student.dormitory && student.dormitory !== dormitory.dormitory && (
        <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-0.5 rounded-full whitespace-nowrap">
          In: {student.dormitory}
        </span>
      )}
      {student.dormitory === dormitory.dormitory && (
        <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded-full whitespace-nowrap">
          Already here
        </span>
      )}
    </label>
  );

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Students to {dormitory.dormitory}</DialogTitle>
          <DialogDescription>
            {mode === "individual" && "Search and assign individual students"}
            {mode === "bulk" && step === "primary" && "Step 1: Select class and gender for bulk assignment"}
            {mode === "bulk" && step === "review" && "Review students — uncheck any exceptions"}
            {mode === "bulk" && step === "secondary" && "Step 2 (Optional): Add individual students from other classes"}
          </DialogDescription>
        </DialogHeader>

        {/* Current assigned count */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Currently assigned: <span className="font-semibold text-foreground">{dormitory.total_students}</span> students
          </span>
          <div className="flex items-center gap-2">
            <Label htmlFor="bulk-toggle" className="text-xs text-muted-foreground cursor-pointer">Bulk mode</Label>
            <button
              id="bulk-toggle"
              role="switch"
              aria-checked={mode === "bulk"}
              onClick={() => {
                setMode(mode === "bulk" ? "individual" : "bulk");
                setStep("primary");
                setIndividualSearch("");
                setIndividualSelected(new Set());
                setPrimarySelected(new Set());
                setSecondarySelected(new Set());
                setSelectedClass("");
                setSelectedSex("");
              }}
              className={cn(
                "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                mode === "bulk" ? "bg-primary" : "bg-muted"
              )}
            >
              <span className={cn(
                "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform",
                mode === "bulk" ? "translate-x-4" : "translate-x-0"
              )} />
            </button>
          </div>
        </div>

        {/* Dorm Mother Info Bar */}
        <div className="border rounded-md p-3 bg-muted/30 space-y-1">
          {editingMother ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Mother's name"
                  value={motherName}
                  onChange={(e) => setMotherName(e.target.value)}
                  className="h-8 text-sm"
                />
                <Input
                  placeholder="Contact number"
                  value={motherContact}
                  onChange={(e) => setMotherContact(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  setMotherName(dormitory.dormitory_mother || "");
                  setMotherContact(dormitory.dormitory_mother_contact || "");
                  setEditingMother(false);
                }}>
                  <XIcon className="h-3 w-3 mr-1" /> Cancel
                </Button>
                <Button size="sm" className="h-7 px-2" disabled={updateMotherMutation.isPending} onClick={() => updateMotherMutation.mutate()}>
                  <Check className="h-3 w-3 mr-1" /> Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">Mother:</span> {dormitory.dormitory_mother || "Not assigned"}
                <span className="mx-2 text-muted-foreground">|</span>
                <span className="font-medium">Contact:</span> {dormitory.dormitory_mother_contact || "—"}
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingMother(true)}>
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Individual mode (default) */}
        {mode === "individual" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Type student name to search..."
                value={individualSearch}
                onChange={(e) => setIndividualSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            {individualSelected.size > 0 && (
              <p className="text-sm font-medium text-primary">
                {individualSelected.size} student(s) selected for assignment
              </p>
            )}
            <ScrollArea className="h-[300px] border rounded-md">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !individualSearch.trim() ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Start typing a student's name to search...
                </div>
              ) : individualCandidates.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No students found
                </div>
              ) : (
                <div className="divide-y">
                  {individualCandidates.map((s) =>
                    renderStudentRow(s, individualSelected.has(s.id), () => toggleIndividual(s.id), true)
                  )}
                </div>
              )}
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={resetAndClose}>Cancel</Button>
              <Button
                disabled={individualSelected.size === 0 || assignIndividualMutation.isPending}
                onClick={() => assignIndividualMutation.mutate()}
              >
                {assignIndividualMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
                ) : (
                  `Assign ${individualSelected.size} Student(s)`
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {mode === "bulk" && step === "primary" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Class</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <option value="">Select class...</option>
                  {classes?.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Sex / Gender</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={selectedSex}
                  onChange={(e) => setSelectedSex(e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
            </div>
            {selectedClass && selectedSex && (
              <p className="text-sm text-muted-foreground">
                {primaryCandidates.length} student(s) match this criteria
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={resetAndClose}>Cancel</Button>
              <Button
                disabled={!selectedClass || !selectedSex || primaryCandidates.length === 0}
                onClick={handlePreview}
              >
                Preview & Review ({primaryCandidates.length})
              </Button>
            </DialogFooter>
          </div>
        )}

        {mode === "bulk" && step === "review" && (
           <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={reviewSearch}
                onChange={(e) => setReviewSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded-full">Already here: {alreadyInThisDorm.length}</span>
              <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-0.5 rounded-full">In other dorm: {alreadyInOtherDorm.length}</span>
              <span className="px-2 py-0.5">Unassigned: {unassigned.length}</span>
              <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Selected: {primarySelected.size}</span>
            </div>
            <ScrollArea className="h-[300px] border rounded-md">
              {(() => {
                const rq = reviewSearch.toLowerCase().trim();
                const filterFn = (s: StudentForAssignment) => {
                  if (!rq) return true;
                  const name = `${s.surname} ${s.other_names || ""}`.toLowerCase();
                  return name.includes(rq);
                };
                const filteredOther = alreadyInOtherDorm.filter(filterFn);
                const filteredHere = alreadyInThisDorm.filter(filterFn);
                const filteredNone = unassigned.filter(filterFn);
                return (
              <div className="divide-y">
                {filteredOther.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-950 text-xs font-semibold text-yellow-800 dark:text-yellow-200">
                      ⚠ Currently in another dormitory — will be moved
                    </div>
                    {filteredOther.map((s) =>
                      renderStudentRow(s, primarySelected.has(s.id), () => togglePrimary(s.id), true)
                    )}
                  </>
                )}
                {filteredHere.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-green-50 dark:bg-green-950 text-xs font-semibold text-green-800 dark:text-green-200">
                      ✓ Already in {dormitory.dormitory}
                    </div>
                    {filteredHere.map((s) =>
                      renderStudentRow(s, primarySelected.has(s.id), () => togglePrimary(s.id))
                    )}
                  </>
                )}
                {filteredNone.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-muted text-xs font-semibold">
                      No dormitory assigned
                    </div>
                    {filteredNone.map((s) =>
                      renderStudentRow(s, primarySelected.has(s.id), () => togglePrimary(s.id))
                    )}
                  </>
                )}
                {filteredOther.length === 0 && filteredHere.length === 0 && filteredNone.length === 0 && (
                  <div className="p-4 text-center text-muted-foreground text-sm">No students match your search</div>
                )}
              </div>
                );
              })()}
            </ScrollArea>
            <DialogFooter className="flex-row gap-2 sm:justify-between">
              <Button variant="outline" onClick={() => setStep("primary")}>Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep("secondary")}>
                  + Add More Students
                </Button>
                <Button
                  disabled={primarySelected.size === 0 || assignMutation.isPending}
                  onClick={() => assignMutation.mutate()}
                >
                  {assignMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
                  ) : (
                    `Assign ${primarySelected.size} Student(s)`
                  )}
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}

        {mode === "bulk" && step === "secondary" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or class..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Primary: {primarySelected.size} selected | Additional: {secondarySelected.size} selected
            </p>
            <ScrollArea className="h-[300px] border rounded-md">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : secondaryCandidates.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  {searchQuery ? "No students match your search" : "No additional students available"}
                </div>
              ) : (
                <div className="divide-y">
                  {secondaryCandidates.map((s) =>
                    renderStudentRow(s, secondarySelected.has(s.id), () => toggleSecondary(s.id), true)
                  )}
                </div>
              )}
            </ScrollArea>
            <DialogFooter className="flex-row gap-2 sm:justify-between">
              <Button variant="outline" onClick={() => { setStep("review"); setSearchQuery(""); }}>
                Back to Review
              </Button>
              <Button
                disabled={(primarySelected.size + secondarySelected.size) === 0 || assignMutation.isPending}
                onClick={() => assignMutation.mutate()}
              >
                {assignMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
                ) : (
                  `Assign ${primarySelected.size + secondarySelected.size} Student(s)`
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ReassignDormSelect = ({
  options,
  value,
  onChange,
}: {
  options: DormitoryVisits[];
  value: string;
  onChange: (val: string) => void;
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = options.filter((d) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (d.dormitory || "").toLowerCase().includes(q) ||
      (d.dormitory_mother || "").toLowerCase().includes(q)
    );
  });

  const selectedLabel = useMemo(() => {
    if (!value) return "Unassign (no dormitory)";
    const parts = value.split("|||");
    const match = options.find(
      (d) => d.dormitory === parts[0] && d.dormitory_mother === (parts[1] || null)
    );
    if (match) return `${match.dormitory} — ${match.dormitory_mother || "No mother"} — ${match.total_students} students, ${match.total_visits} visits`;
    return value;
  }, [value, options]);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between text-left font-normal h-auto min-h-[40px] whitespace-normal"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm truncate">{selectedLabel}</span>
        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg">
          <div className="p-2">
            <Input
              placeholder="Search dormitory..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <ScrollArea className="max-h-[200px]">
            <div
              className="px-3 py-2 text-sm cursor-pointer hover:bg-muted rounded-sm mx-1"
              onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
            >
              Unassign (no dormitory)
            </div>
            {filtered.map((d, i) => {
              const val = `${d.dormitory}|||${d.dormitory_mother}|||${d.dormitory_mother_contact}`;
              return (
                <div
                  key={`${d.dormitory}-${d.dormitory_mother}-${i}`}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-muted rounded-sm mx-1 ${value === val ? "bg-muted font-medium" : ""}`}
                  onClick={() => { onChange(val); setOpen(false); setSearch(""); }}
                >
                  <span className="font-medium">{d.dormitory}</span>
                  <span className="text-muted-foreground"> — {d.dormitory_mother || "No mother"} — {d.total_students} students, {d.total_visits} visits</span>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">No match</div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export const DormitoryMotherManager = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedDormitory, setSelectedDormitory] = useState<DormitoryVisits | null>(null);
  const [assignDormitory, setAssignDormitory] = useState<DormitoryVisits | null>(null);
  const [deleteDormitory, setDeleteDormitory] = useState<DormitoryVisits | null>(null);
  const [targetDormitory, setTargetDormitory] = useState<string>("");
  const [deleteStudents, setDeleteStudents] = useState<StudentForAssignment[]>([]);
  const [reassignSelected, setReassignSelected] = useState<Set<string>>(new Set());
  const [deleteStep, setDeleteStep] = useState<"confirm" | "reassign">("confirm");

  // Fetch students for the dormitory being deleted
  const fetchDormStudents = async (dorm: DormitoryVisits) => {
    let query = supabase
      .from("students")
      .select("id, surname, other_names, class, stream, dormitory, sex, is_archived")
      .eq("school_id", user?.schoolId!)
      .eq("dormitory", dorm.dormitory);
    
    if (dorm.dormitory_mother) {
      query = query.eq("dormitory_mother", dorm.dormitory_mother);
    } else {
      query = query.is("dormitory_mother", null);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("Error fetching students:", error);
      return;
    }
    setDeleteStudents((data as StudentForAssignment[]) || []);
    setReassignSelected(new Set((data || []).map((s: any) => s.id)));
  };

  const handleOpenDelete = (dorm: DormitoryVisits) => {
    setDeleteDormitory(dorm);
    setDeleteStep("confirm");
    setTargetDormitory("");
    setReassignSelected(new Set());
    fetchDormStudents(dorm);
  };

  const deleteDormMutation = useMutation({
    mutationFn: async ({ reassignTo, studentIds }: { reassignTo: { dormitory: string; mother: string | null; contact: string | null } | null; studentIds: string[] }) => {
      if (studentIds.length === 0) return;
      
      if (reassignTo) {
        const { error } = await supabase
          .from("students")
          .update({
            dormitory: reassignTo.dormitory,
            dormitory_mother: reassignTo.mother,
            dormitory_mother_contact: reassignTo.contact,
          })
          .in("id", studentIds);
        if (error) throw error;
      } else {
        // Unassign all
        const { error } = await supabase
          .from("students")
          .update({
            dormitory: null,
            dormitory_mother: null,
            dormitory_mother_contact: null,
          })
          .in("id", studentIds);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Dormitory removed and students updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      queryClient.invalidateQueries({ queryKey: ["students-for-dorm-assignment"] });
      setDeleteDormitory(null);
      setDeleteStudents([]);
    },
    onError: (err) => {
      console.error("Delete error:", err);
      toast({ title: "Error", description: "Failed to remove dormitory", variant: "destructive" });
    },
  });

  const { data: dormitoryMothers, isLoading } = useQuery({
    queryKey: ["dormitory-mothers", user?.schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dormitory_mother_visits")
        .select("*");

      if (error) throw error;
      return data as DormitoryVisits[];
    },
    enabled: !!user?.schoolId,
  });

  const [dormSearch, setDormSearch] = useState("");

  // Filter out entries with null/empty dormitory names, sort alphabetically, and apply search
  const validDormitories = useMemo(() => {
    const filtered = dormitoryMothers?.filter(
      (d) => d.dormitory && d.dormitory.trim() !== ""
    ) || [];
    
    // Sort alphabetically by dormitory name
    const sorted = filtered.sort((a, b) => 
      (a.dormitory || "").localeCompare(b.dormitory || "")
    );

    // Apply search filter
    if (!dormSearch.trim()) return sorted;
    const q = dormSearch.toLowerCase();
    return sorted.filter(
      (d) =>
        (d.dormitory || "").toLowerCase().includes(q) ||
        (d.dormitory_mother || "").toLowerCase().includes(q)
    );
  }, [dormitoryMothers, dormSearch]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dormitory Mothers</CardTitle>
        <CardDescription>
          Manage dormitory mother assignments and view their statistics
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center">Loading dormitory data...</div>
        ) : (dormitoryMothers?.length || 0) === 0 ? (
          <div className="text-center text-muted-foreground">
            No dormitories found
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search dormitory or mother name..."
                value={dormSearch}
                onChange={(e) => setDormSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {validDormitories.length === 0 ? (
              <div className="text-center text-muted-foreground py-4">
                No dormitories match your search
              </div>
            ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {validDormitories.map((dormitory, idx) => (
              <Card key={`${dormitory.dormitory}-${dormitory.dormitory_mother}-${idx}`}>
                <CardHeader>
                  <CardTitle className="text-lg">{dormitory.dormitory}</CardTitle>
                  <CardDescription>
                    {dormitory.total_students} students | {dormitory.total_visits} visits
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Mother:</span>{" "}
                      {dormitory.dormitory_mother || "Not assigned"}
                    </div>
                    <div>
                      <span className="font-medium">Contact:</span>{" "}
                      {dormitory.dormitory_mother_contact || "Not provided"}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button
                        className="flex-1"
                        onClick={() => setSelectedDormitory(dormitory)}
                      >
                        Edit Assignment
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setAssignDormitory(dormitory)}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Assign Students
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleOpenDelete(dormitory)}
                        title="Delete dormitory"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
            )}
          </div>
        )}
      </CardContent>
      {selectedDormitory && (
        <EditDormitoryDialog
          dormitory={selectedDormitory}
          isOpen={!!selectedDormitory}
          onClose={() => setSelectedDormitory(null)}
        />
      )}
      {assignDormitory && (
        <AssignStudentsDialog
          dormitory={assignDormitory}
          isOpen={!!assignDormitory}
          onClose={() => setAssignDormitory(null)}
        />
      )}
      {deleteDormitory && (
        <Dialog open={!!deleteDormitory} onOpenChange={() => { setDeleteDormitory(null); setDeleteStudents([]); setDeleteStep("confirm"); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Delete Dormitory "{deleteDormitory.dormitory}"</DialogTitle>
              <DialogDescription>
                {deleteDormitory.dormitory_mother && `Mother: ${deleteDormitory.dormitory_mother}`}
                {deleteDormitory.dormitory_mother_contact && ` | Contact: ${deleteDormitory.dormitory_mother_contact}`}
              </DialogDescription>
            </DialogHeader>

            {deleteStudents.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-medium">{deleteStudents.length} student(s) assigned:</p>
                <ScrollArea className="max-h-[250px] border rounded-md">
                  <div className="divide-y">
                    {deleteStudents.map((s) => (
                      <label key={s.id} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50 cursor-pointer">
                        <Checkbox
                          checked={reassignSelected.has(s.id)}
                          onCheckedChange={() => {
                            setReassignSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                              return next;
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{s.surname} {s.other_names || ""} {(s as any).is_archived && <span className="text-xs text-muted-foreground">(archived)</span>}</p>
                          <p className="text-xs text-muted-foreground">{s.class || "No class"}{s.stream ? ` - ${s.stream}` : ""}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </ScrollArea>

                <div className="space-y-2">
                  <Label>Reassign checked students to:</Label>
                  <ReassignDormSelect
                    options={validDormitories.filter((d) => !(d.dormitory === deleteDormitory.dormitory && d.dormitory_mother === deleteDormitory.dormitory_mother && d.dormitory_mother_contact === deleteDormitory.dormitory_mother_contact))}
                    value={targetDormitory}
                    onChange={setTargetDormitory}
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No students are assigned to this dormitory entry.</p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteDormitory(null); setDeleteStudents([]); }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteDormMutation.isPending}
                onClick={() => {
                  // All student IDs from this dormitory entry
                  const allIds = deleteStudents.map((s) => s.id);
                  
                  if (allIds.length === 0) {
                    // No students, nothing to update — just close
                    setDeleteDormitory(null);
                    setDeleteStudents([]);
                    toast({ title: "Done", description: "Dormitory entry removed" });
                    queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
                    return;
                  }

                  const reassignIds = [...reassignSelected];
                  const unassignIds = allIds.filter((id) => !reassignSelected.has(id));

                  // Parse composite target value: "dormName|||mother|||contact"
                  let parsedTarget: { dormitory: string; mother: string | null; contact: string | null } | null = null;
                  if (targetDormitory) {
                    const parts = targetDormitory.split("|||");
                    parsedTarget = {
                      dormitory: parts[0],
                      mother: parts[1] || null,
                      contact: parts[2] || null,
                    };
                  }

                  const promises: Promise<any>[] = [];

                  if (reassignIds.length > 0 && parsedTarget) {
                    promises.push(
                      deleteDormMutation.mutateAsync({
                        reassignTo: parsedTarget,
                        studentIds: reassignIds,
                      })
                    );
                  } else {
                    unassignIds.push(...reassignIds);
                  }

                  if (unassignIds.length > 0) {
                    promises.push(
                      deleteDormMutation.mutateAsync({
                        reassignTo: null,
                        studentIds: unassignIds,
                      })
                    );
                  }

                  Promise.all(promises).then(() => {
                    queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
                    queryClient.invalidateQueries({ queryKey: ["students-for-dorm-assignment"] });
                    setDeleteDormitory(null);
                    setDeleteStudents([]);
                    toast({ title: "Success", description: "Dormitory removed and students updated" });
                  }).catch(() => {
                    // Error already handled by mutation's onError
                  });
                }}
              >
                {deleteDormMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>
                ) : deleteStudents.length === 0 ? (
                  "Delete Dormitory"
                ) : (
                  `Delete & ${targetDormitory ? `Reassign ${reassignSelected.size}` : "Unassign All"}`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
